// ============================================================
// DetailChrome 상태별 오버라이드 해소 (순수 함수, React 무관)
// ============================================================
//
// composed 보고서 상세(useComposedReportActions)가 만든 "태깅된 액션" 배열을
// 템플릿 `detailChrome`(DetailChromeConfig)의 상태별 표시 오버라이드로 해소해
// headerLeft/headerRight/actionBar 3슬롯의 StickyHeaderAction[]로 분류·정렬한다.
//
// - **동작(behavior)은 코드 소유** — 여기선 표시(노출/슬롯/순서/라벨)만 오버라이드.
// - mandatory 액션(라이프사이클)은 hidden 오버라이드를 무시(강제 노출) — 숨김 불가.
// - config 미지정/항목 미선언이면 태깅의 defaultSlot + 코드 레지스트리
//   (DETAIL_CHROME_ACTIONS)의 defaultOrder로 배치 → 회귀 안전.
// - 커스텀 link 아이템(actionId 없음)은 이번 범위 제외(태깅에 없어 자연히 무시).
//   display 필드도 이번 미사용.
// 결정: docs/decisions/2026-07-21-compose-chrome-per-state-detail
//
// ⚠️ 배치 결정(노출/슬롯/순서)은 report-composer의 resolveDetailChromePlacement가 단일 소스다 —
// admin WYSIWYG 미리보기(resolveDetailChromeLayout)와 동일 코어를 써 미리보기↔실제 렌더 패리티를 보장.
// 이 함수는 그 결정 위에 런타임 태깅 액션(권한/substate 게이팅됨)을 얹어 StickyHeaderAction으로 배치한다.

import {
  resolveDetailChromePlacement,
  resolveDetailChromeUnsupportedReason,
  splitDetailChromeByFit,
  type DetailChromeTemplateCapabilities,
} from './detailChromeResolve';
import type { RenderData } from './types';
import type {
  DetailChromeActionId,
  DetailChromeConfig,
  DetailChromeOverflow,
  DetailChromeSlot,
  DetailChromeStatus,
} from '@safetyhub/shared';
import type { StickyHeaderAction } from './StickyHeader';

/**
 * 해소 입력 1개 — 코드가 만든 실제 렌더 액션 + 오버라이드 매칭에 필요한 메타.
 * `action`은 라벨 override 대상(그 외 필드는 코드가 확정한 값 그대로 사용).
 */
export interface TaggedDetailAction {
  /** 이 액션의 오버라이드 매칭 id(detailChrome item.actionId와 대조). */
  actionId: DetailChromeActionId;
  /** detailChrome 미지정(또는 항목 미선언) 시 배치될 슬롯. */
  defaultSlot: DetailChromeSlot;
  /** 숨김 불가(라이프사이클 필수) — hidden 오버라이드 무시. */
  mandatory: boolean;
  /** 렌더할 액션(라벨 override 대상). */
  action: StickyHeaderAction;
}

/** 슬롯별 해소 결과 — 3키 모두 존재(빈 배열이라도). */
export type DetailChromeResolved = Record<DetailChromeSlot, StickyHeaderAction[]>;

/** 정렬 완료 후 슬롯 매핑용 내부 엔트리(order/overflow는 정렬·오버플로우 분리에만 사용). */
interface BucketEntry {
  order: number;
  overflow: DetailChromeOverflow;
  action: StickyHeaderAction;
}

/**
 * 하단 액션바 엔트리를 폭 기준으로 노출/오버플로우('더보기' 메뉴)로 합친다(fit 기반 — splitDetailChromeByFit
 * 공유, 미리보기와 동일 추정). `never`(mandatory 포함)=노출, `always`=메뉴, `auto`=들어갈 만큼 노출하고
 * 넘치는 것만 접음. 접힌 게 있으면 collapsed 최소 order 위치에 '더보기'(menuItems) 단일 액션 삽입.
 */
function collapseActionBar(entries: BucketEntry[], availableWidth: number): StickyHeaderAction[] {
  const { visible: vIdx, menu: mIdx } = splitDetailChromeByFit(
    entries.map((e) => ({
      label: typeof e.action.label === 'string' ? e.action.label : '',
      overflow: e.overflow,
    })),
    availableWidth,
  );
  if (mIdx.length === 0) return vIdx.map((i) => entries[i].action);
  const menu = mIdx.map((i) => entries[i]);
  const visible = vIdx.map((i) => entries[i]);

  const overflowEntry: BucketEntry = {
    // entries는 정렬 완료본이라 menu[0].order = 접힌 것 중 최소 order.
    order: menu[0].order,
    overflow: 'never',
    action: {
      key: '__detail-overflow',
      label: '더보기',
      onClick: () => {}, // menuItems 있으면 트리거로 동작(onClick 미사용).
      menuItems: menu.map((e) => ({
        key: e.action.key,
        label: e.action.label,
        onClick: e.action.onClick,
        disabled: e.action.disabled,
        tooltip: e.action.tooltip,
        variant: e.action.variant === 'danger' ? 'danger' : 'secondary',
      })),
    },
  };
  return [...visible, overflowEntry].sort((a, b) => a.order - b.order).map((e) => e.action);
}

/**
 * 태깅된 액션 배열을 상태별 detailChrome 오버라이드로 해소한다.
 *
 * @param tagged 코드가 상태/권한을 이미 게이팅해 만든 액션들(있는 것만).
 * @param config 템플릿 detailChrome(미지정이면 레지스트리 기본 배치).
 * @param status 현재 report.status(coarse 4값) — statusOverrides 매칭 축.
 * @param opts.availableWidth 하단바 가용 폭(px) — fit 기반 오버플로우. 미지정=무한(접지 않음).
 * @param opts.scope 범용 조건(visibleWhen/enableWhen/primaryWhen) 평가용 chrome-scope 변수값. 주어지면
 *                   visibleWhen 미충족 액션 omit, enableWhen으로 disabled/tooltip, primaryWhen으로 variant
 *                   산출(코드 소유 disabled/variant와 결합). 미지정=코드값 그대로(하위호환).
 * @param opts.capabilities 양식 능력(위임 지원·제출 후 흐름 선언). 주어지면 그 능력이 없는 액션은
 *                   태깅돼 있어도 **버린다** — admin 미리보기(`resolveDetailChromeLayout`)와 같은 판정
 *                   (`resolveDetailChromeUnsupportedReason`)을 실제 렌더 경로에도 건다(2026-08-30 리뷰
 *                   INFO — 종전엔 layout에만 있었고 web 훅은 같은 축을 손으로 게이트했다). 훅의 손 게이트가
 *                   먼저 막으므로 정상 경로에선 걸리는 것이 없다 — 능력 축 액션을 훅에 새로 태깅하며
 *                   손 게이트를 빠뜨렸을 때의 안전망이다. 미지정=능력 게이팅 없음(하위호환).
 */
export function resolveDetailChromeActions(
  tagged: TaggedDetailAction[],
  config: DetailChromeConfig | undefined,
  status: DetailChromeStatus,
  opts?: { availableWidth?: number; scope?: RenderData; capabilities?: DetailChromeTemplateCapabilities },
): DetailChromeResolved {
  const buckets: Record<DetailChromeSlot, BucketEntry[]> = {
    headerLeft: [],
    headerRight: [],
    actionBar: [],
  };

  for (const t of tagged) {
    // 양식 능력 게이트 — 미리보기와 같은 판정(위 @param opts.capabilities).
    if (resolveDetailChromeUnsupportedReason(t.actionId, opts?.capabilities)) continue;
    // 배치 결정(노출/슬롯/순서/라벨override/overflow)은 공유 코어가 소유 — null이면 이 상태에서 숨김/비eligible.
    // (mandatory는 코어가 hidden을 무시해 항상 배치 반환 → 강제 노출.) scope 주어지면 visibleWhen 미충족=null.
    const placement = resolveDetailChromePlacement(config, status, t.actionId, opts?.scope);
    if (!placement) continue;

    // 범용 조건(scope) 결과를 코드 소유 값과 결합 — scope 미지정 시 enabled/primary undefined라 코드값 그대로.
    // disabled: 코드 소유 disabled와 AND(둘 중 하나라도 true면 disabled). enabled===false만 반영.
    const disabled = t.action.disabled || placement.enabled === false;
    // tooltip: enableWhen로 비활성된 경우 사유(disabledReason) 우선, 그 외 코드 tooltip.
    const tooltip =
      placement.enabled === false ? (placement.disabledReason ?? t.action.tooltip) : t.action.tooltip;
    // variant: primary는 비-danger에만 반영(danger는 코드 소유 유지). primary 미설정 시 코드값 그대로.
    const primaryVariant: StickyHeaderAction['variant'] = placement.primary ? 'primary' : 'secondary';
    const variant =
      placement.primary !== undefined && t.action.variant !== 'danger'
        ? primaryVariant
        : t.action.variant;

    // enableWhen 비활성 전파(드롭다운 케이스): 라이프사이클 액션(예 submit)은 '결재▾' 드롭다운의
    // 메뉴 항목으로 렌더되며, 드롭다운 트리거/메뉴 항목은 wrapper의 disabled를 반영하지 않는다.
    // 그래서 enableWhen 미충족(placement.enabled===false)이면 **첫 메뉴 항목**(=이 actionId의 실동작,
    // buildApprovalActions가 항상 선두 배치)에도 disabled/tooltip을 전파해야 실제로 게이팅된다.
    // 코드가 이미 비활성한 항목은 그대로 둠(둘 중 하나라도 true면 비활성).
    const menuItems =
      t.action.menuItems && placement.enabled === false
        ? t.action.menuItems.map((mi, i) =>
            i === 0
              ? { ...mi, disabled: true, tooltip: placement.disabledReason ?? mi.tooltip }
              : mi,
          )
        : t.action.menuItems;

    const action: StickyHeaderAction = {
      ...t.action,
      label: placement.labelOverride ?? t.action.label,
      disabled,
      tooltip,
      variant,
      menuItems,
    };

    buckets[placement.slot].push({
      order: placement.order,
      overflow: placement.overflow,
      action,
    });
  }

  const sorted = (arr: BucketEntry[]) => arr.sort((a, b) => a.order - b.order);
  return {
    headerLeft: sorted(buckets.headerLeft).map((e) => e.action),
    // 헤더 우측은 **여기서 접지 않고 `overflow`만 실어 보낸다.** 하단바와 달리 가용 폭이
    // 뷰포트가 아니라 「행 폭 − 좌측(뒤로가기 라벨 + 상태 배지)」이고, 그 좌측은 진입점마다
    // 라벨이 달라 추정이 반드시 어긋난다. 그래서 판정은 DOM을 가진 `StickyHeader`가
    // 실측(ResizeObserver)으로 하고, **정책(never/auto/always)은 레지스트리가 그대로 소유**한다
    // — 같은 `splitDetailChromeByFit`를 쓰므로 규칙이 두 벌이 되지 않는다.
    // (2026-09-10: 종전엔 헤더가 접지 않아 폴드에서 위임 5종이 전부 인라인으로 서고 뒤로가기
    //  링크가 문자 단위로 무너졌다. 「StickyHeader 자체 모바일 처리」는 **폰 전용**이었다.)
    headerRight: sorted(buckets.headerRight).map((e) => ({ ...e.action, overflow: e.overflow })),
    // 하단바는 뷰포트 폭이 곧 가용 폭이라 여기서 접는다. 폭 미지정=무한(접지 않음).
    actionBar: collapseActionBar(
      sorted(buckets.actionBar),
      opts?.availableWidth ?? Number.POSITIVE_INFINITY,
    ),
  };
}
